import {
  IProcedure,
  ProcedureDocumentType,
  ProcedureRecipient,
  ProcedureState
} from './Procedure';
import moment from 'moment';
import { IUser } from './User';
import { ProcedureMachine } from './ProcedureMachine';
import { RolesLogic } from 'common/permissions/RolesLogic';
import { Roles } from 'common/permissions/Roles';
import { getWidgetSubtype, WidgetType } from './widget/Widget';
import { WidgetInputType } from './widget/input/WidgetInputType';

export class ProcedureLogic {
  /**
   * Ottiene lo stato reale di una domanda.
   */
  // TODO Controllare gestione date con TimeZone
  static getState(procedure: IProcedure | undefined) {
    if (!procedure) return ProcedureState.Draft;

    const now = moment();
    if (procedure.state === ProcedureState.Published) {
      // Pronta (non modificabile, ma non visibile)
      if (now.isBefore(procedure.publishedAt)) {
        return ProcedureState.Ready;
      }
      // In fase di valutazione (graduatorie)
      // Se è dopo la scadenza ma prima del termine della valutazione (se presente)
      if (
        now.isSameOrAfter(procedure.expiresAt) &&
        procedure.evaluatedAt &&
        now.isBefore(procedure.evaluatedAt)
      ) {
        return ProcedureState.Evaluating;
      }
      // Scaduta
      // Se è dopo la data di scadenza e non c'è un termine della valutazione
      // oppure se è dopo il termine della valutazione
      if (
        (now.isSameOrAfter(procedure.expiresAt) && !procedure.evaluatedAt) ||
        now.isSameOrAfter(procedure.evaluatedAt)
      ) {
        return ProcedureState.Expired;
      }
      // Attiva (visibile e compilabile)
      if (now.isSameOrAfter(procedure.activatedAt)) {
        return ProcedureState.Active;
      }
      // Pubblicata (visibile ma non compilabile)
      if (now.isSameOrAfter(procedure.publishedAt)) {
        return ProcedureState.Published;
      }
    }

    return procedure.state;
  }

  /**
   * Controlla se una domanda richiede la protocollazione
   */
  static requiresDocumentRegistration(procedure: IProcedure) {
    return (
      procedure.documentType === ProcedureDocumentType.Registered ||
      procedure.documentType === ProcedureDocumentType.Extended
    );
  }

  /**
   * Controlla che l'utente possa accedere alla procedure
   */
  static allowsView(
    procedure: IProcedure,
    user: IUser
  ): { allowed: boolean; message?: string } {
    const userRole = RolesLogic.get(user.role);
    // Check utenti cittadino
    // La domanda deve essere visibile al cittadino, e se è in test, dev'essere accessibile solo ai tester.
    if (userRole === Roles.Citizen || userRole === Roles.Anonymous) {
      if (!ProcedureMachine.allows(procedure, 'citizen.view')) {
        return {
          allowed: false,
          message: 'Impossibile accedere alla domanda. (Codice PA01)'
        };
      }
      if (procedure.state === ProcedureState.Test && !user.isTester) {
        return {
          allowed: false,
          message: 'Impossibile accedere alla domanda. (Codice PA02)'
        };
      }

      // Soltanto gli utenti tester possono accedere alle domande in collaudo
      if (procedure.state === ProcedureState.Test && !user.isTester) {
        return {
          allowed: false,
          message: 'Impossibile accedere alla domanda. (Codice PA05)'
        };
      }

      // Limita l'accesso alle domande per gli Employee ai dipendenti
      if (
        procedure.recipient === ProcedureRecipient.Employee &&
        !user.isEmployee
      ) {
        return {
          allowed: false,
          message: 'Impossibile accedere alla domanda. (Codice PA06)'
        };
      }

      // Limita l'accesso alle domande per i cittadini ai non impiegati
      if (
        procedure.recipient === ProcedureRecipient.Employee &&
        !user.isEmployee
      ) {
        return {
          allowed: false,
          message: 'Impossibile accedere alla domanda. (Codice PA07)'
        };
      }

      // Limita l'accesso alle domande per gli impiegati agli impiegati stessi
      if (
        procedure.recipient === ProcedureRecipient.Citizen &&
        user.isEmployee
      ) {
        return {
          allowed: false,
          message: 'Impossibile accedere alla domanda. (Codice PA08)'
        };
      }

      return { allowed: true };
    }

    /* SE l'utente non è un cittadino (ruolo backoffice) */

    // Se si ha accesso alla visualizzazione di tutte le procedure
    if (userRole?.hasPermission('procedure.read')) {
      return { allowed: true };
    }

    // Se si ha accesso alla visualizzazione solo delle procedure
    // di cui si è commissari
    if (userRole?.hasPermission('procedure.read:commission')) {
      const procedureState = this.getState(procedure);

      // La domanda dell'istanza deve essere "In Corso"
      // Deve esistere la commissione
      // L'utente deve far parte della commissione
      const allowed =
        procedureState === ProcedureState.Evaluating &&
        !!procedure.commission &&
        procedure.commission.some(commissioner => commissioner.id === user.id);

      return {
        allowed,
        message: allowed
          ? undefined
          : "Impossibile accedere all'istanza: accesso negato. (Codice PA03)"
      };
    }

    // Se si ha accesso solo alle procedure di cui si è consultatori
    if (userRole?.hasPermission('procedure.read:consultation')) {
      const allowed =
        !!procedure.consultants &&
        procedure.consultants.some(c => c.id === user.id);

      return {
        allowed,
        message: allowed
          ? undefined
          : "Impossibile accedere all'istanza: accesso negato. (Codice PA03)"
      };
    }

    return {
      allowed: false,
      message: 'Impossibile accedere alla domanda. (Codice PA04)'
    };
  }

  /**
   * Ottiene l'ID di raggruppamento di questa domanda.
   * - Se è subordinata, l'id è registrato in `parentId`
   */
  static getGroupId(procedure: IProcedure) {
    return procedure.groupId;
  }

  /**
   * Esclusivamente per la **visualizzazione**, procediamo a sottrarre un giorno
   * se l'ora di scadenza è a mezzanotte esatta.
   * Questo perché il token di moment "24" non modifica il giorno
   */
  static getRenderableDateWith24Hour(input: Date | null | undefined) {
    if (!input) return null;

    const date = moment(input);
    if (date.format('kk:mm') === '24:00') {
      return date.subtract(1, 'day').toDate();
    }
    return date.toDate();
  }

  /**
   * Controlla se ci sono incongruenze nelle opzioni della commissione.
   * Ritorna vero se la procedura non ha una commissione ma
   * è impostata una tipologia di assegnamento punti.
   */
  static hasCommissionOptionIncongruence(procedure: IProcedure) {
    return (
      !!procedure.pointAssignmentType &&
      (!procedure.commission || procedure.commission.length === 0)
    );
  }

  /**
   * Ritorna il documento principale tra i procedureDocuments della domanda
   */
  static getMainDocument(procedure: IProcedure) {
    if (!procedure.documents) {
      return null;
    }
    return procedure.documents.find(document => document.isMain);
  }

  /**
   * Ritorna gli allegati (procedureDocument) tra i procedureDocuments della domanda
   */
  static getProcedureDocumentAttachment(procedure: IProcedure) {
    if (!procedure.documents) {
      return [];
    }
    return procedure.documents.filter(document => !document.isMain);
  }

  /**
   * Controlla che il documento sia eliminabile.
   * E.g. che non sia connesso a nessun widget
   */
  static canDeleteDocument(procedure: IProcedure, documentId: number) {
    // Cerca il widget di tipo input ha come documentId quello del documento in esame
    const widgetConnected = procedure.widgets
      .filter(widget => widget.type === WidgetType.Input)
      .find(widget => widget.input?.extra?.documentId === documentId);

    if (widgetConnected) {
      return {
        result: false,
        message: `Il documento è connesso al widget "${widgetConnected.input?.name}"`
      };
    }

    return { result: true };
  }

  /** Controlla che il campo laws sia vuoto */
  static isLawsEmpty(laws: string | null | undefined) {
    // Retrocompatibilità
    // Di default Quill utilizza `<p><br></p>` come "documento vuoto"
    // https://github.com/quilljs/quill/issues/1235#issuecomment-273044116

    //È vuota se non sono presenti laws (null/undefined) oppure se è l'indicatore speciale
    return !laws || laws === '<p><br></p>';
  }
}
